/**
 * Estado del studio AI Designs colgado en un moodboard Kind=ai_design
 * (ResultSummary JSON) y enlace de vuelta desde ficha/moodboard hijo.
 */

/** Tope de ítems en el panel derecho (antes 24; se perdían generaciones viejas). */
export const AI_DESIGN_HISTORY_LIMIT = 100;

/** Días que se conservan fotos del historial (cron diario en BACK + AI-SERVICE). */
export const AI_DESIGN_HISTORY_RETENTION_DAYS = 15;

/** Tope razonable de prompt (evita pegar ficheros/binario y bloquear el TextField). */
export const AI_DESIGN_PROMPT_MAX_CHARS = 4000;

/**
 * No persistir data: URLs en ResultSummary (refs en base64 → 10–20 MB y el BACK
 * corta en 20mb; el guardado falla y parece que “no guarda el historial”).
 */
export function persistablePreviewUrl(previewUrl) {
  const u = String(previewUrl || "").trim();
  if (!u) return null;
  if (/^data:/i.test(u)) return null;
  return u;
}

/**
 * Limpia prompts corruptos (binario / base64 pegado por error).
 * No hace trim de extremos en vivo: si no, al escribir un espacio al final
 * onChange lo borra y parece que "no deja poner espacios".
 * Usa trimEnds:true al persistir / hidratar.
 */
export function sanitizeStudioPrompt(
  raw,
  { maxChars = AI_DESIGN_PROMPT_MAX_CHARS, trimEnds = false } = {},
) {
  let s = String(raw ?? "");
  if (!s) return "";
  // Quitar NUL y controles (excepto \t \n \r)
  s = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
  const trimmedProbe = s.trim();
  if (
    /^data:/i.test(trimmedProbe) ||
    /base64,/i.test(trimmedProbe.slice(0, 80))
  ) {
    return "";
  }
  // Muchos no-imprimibles → basura de fichero pegado
  const sample = s.slice(0, 500);
  if (sample.length >= 40) {
    const weird = (sample.match(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\uFFFF]/g) || [])
      .length;
    if (weird / sample.length > 0.12) return "";
  }
  // Base64 largo sin espacios (p.ej. bUpX/aUCiHU1…)
  const compact = s.replace(/\s+/g, "");
  if (
    compact.length > 200 &&
    /^[A-Za-z0-9+/=_-]+$/.test(compact.slice(0, 400)) &&
    (s.match(/\s/g) || []).length < Math.max(3, s.length / 80)
  ) {
    return "";
  }
  const max = Math.max(200, Number(maxChars) || AI_DESIGN_PROMPT_MAX_CHARS);
  if (s.length > max) s = s.slice(0, max);
  return trimEnds ? s.trim() : s;
}

export function parseAiDesignStudioSummary(raw) {
  if (raw == null || raw === "") return null;
  let obj = raw;
  if (typeof raw === "string") {
    const t = String(raw).trim();
    if (!t || t[0] !== "{") return null;
    try {
      obj = JSON.parse(t);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object") return null;
  if (obj.kind !== "ai_design_studio") return null;
  const pushedRaw =
    obj.pushedToBoards && typeof obj.pushedToBoards === "object"
      ? obj.pushedToBoards
      : {};
  const pushedToBoards = {};
  for (const [boardId, ids] of Object.entries(pushedRaw)) {
    if (!Array.isArray(ids)) continue;
    pushedToBoards[String(boardId)] = ids
      .map((id) => String(id || "").trim())
      .filter(Boolean);
  }
  const noticeRaw =
    obj.retentionNotice && typeof obj.retentionNotice === "object"
      ? obj.retentionNotice
      : null;
  const retentionNotice = noticeRaw
    ? {
        pending: Boolean(noticeRaw.pending),
        purgedAt: noticeRaw.purgedAt || null,
        purgedCount: Number(noticeRaw.purgedCount) || 0,
        retentionDays:
          Number(noticeRaw.retentionDays) || AI_DESIGN_HISTORY_RETENTION_DAYS,
        acknowledgedAt: noticeRaw.acknowledgedAt || null,
      }
    : null;
  return {
    v: Number(obj.v) || 1,
    designKind: String(obj.designKind || "").trim() || null,
    prompt: sanitizeStudioPrompt(obj.prompt || "", { trimEnds: true }),
    name: String(obj.name || ""),
    genHistory: Array.isArray(obj.genHistory) ? obj.genHistory : [],
    selectedHistoryId: obj.selectedHistoryId || null,
    pushedToBoards,
    retentionNotice,
  };
}

/** Marca el aviso de retención como visto (para PATCH ResultSummary). */
export function acknowledgeRetentionNoticeInSummary(raw) {
  const studio = parseAiDesignStudioSummary(raw);
  if (!studio?.retentionNotice?.pending) return null;
  let obj;
  try {
    obj = typeof raw === "string" ? JSON.parse(raw) : { ...raw };
  } catch {
    return null;
  }
  return JSON.stringify({
    ...obj,
    retentionNotice: {
      ...(obj.retentionNotice || {}),
      pending: false,
      acknowledgedAt: new Date().toISOString(),
    },
  });
}

export function buildAiDesignStudioSummary({
  designKind = null,
  prompt = "",
  name = "",
  genHistory = [],
  selectedHistoryId = null,
  pushedToBoards = null,
  retentionNotice = null,
} = {}) {
  const history = (Array.isArray(genHistory) ? genHistory : [])
    .slice(0, AI_DESIGN_HISTORY_LIMIT)
    .map((item) => ({
      id: item?.id || null,
      filename: item?.filename || null,
      relativeUrl: item?.relativeUrl || null,
      pngRelativeUrl: item?.pngRelativeUrl || null,
      pngFilename: item?.pngFilename || null,
      previewUrl: persistablePreviewUrl(item?.previewUrl),
      format: item?.format || null,
      driveFileUrl: item?.driveFileUrl || null,
      vectorNote: item?.vectorNote || null,
      prompt:
        sanitizeStudioPrompt(item?.prompt || "", { trimEnds: true }) || null,
      mode: item?.mode || null,
      view: item?.view || null,
      createdAt: item?.createdAt || null,
    }))
    .filter(
      (item) => item.relativeUrl || item.pngRelativeUrl || item.previewUrl,
    );
  const pushed = {};
  if (pushedToBoards && typeof pushedToBoards === "object") {
    for (const [boardId, ids] of Object.entries(pushedToBoards)) {
      if (!Array.isArray(ids)) continue;
      const clean = [
        ...new Set(ids.map((id) => String(id || "").trim()).filter(Boolean)),
      ];
      if (clean.length) pushed[String(boardId)] = clean;
    }
  }
  const payload = {
    v: 1,
    kind: "ai_design_studio",
    designKind: designKind || null,
    prompt: sanitizeStudioPrompt(prompt || "", { trimEnds: true }),
    name: String(name || ""),
    genHistory: history,
    selectedHistoryId: selectedHistoryId || null,
    pushedToBoards: pushed,
  };
  if (retentionNotice && typeof retentionNotice === "object") {
    payload.retentionNotice = {
      pending: Boolean(retentionNotice.pending),
      purgedAt: retentionNotice.purgedAt || null,
      purgedCount: Number(retentionNotice.purgedCount) || 0,
      retentionDays:
        Number(retentionNotice.retentionDays) ||
        AI_DESIGN_HISTORY_RETENTION_DAYS,
      acknowledgedAt: retentionNotice.acknowledgedAt || null,
    };
  }
  return JSON.stringify(payload);
}

export function readSourceAiDesignId(row) {
  const direct =
    row?.SourceAiDesignId ??
    row?.sourceAiDesignId ??
    row?.SourceAiDesignMoodboardId;
  const n = Number(direct);
  if (Number.isFinite(n) && n > 0) return n;
  return null;
}

/**
 * Historial aún no volcado a una ficha/moodboard.
 * Si nunca se trackeó ese board, solo ítems posteriores a su creación
 * (evita re-subir lo que ya entró al crear la ficha).
 *
 * @param {Array<{ id?: string, createdAt?: number }>} history
 * @param {string[]|null|undefined} trackedIds null/undefined = sin baseline
 * @param {number} boardCreatedMs
 */
export function selectPendingHistoryForBoard(
  history = [],
  trackedIds = null,
  boardCreatedMs = 0,
) {
  const hasTracked = Array.isArray(trackedIds);
  const already = new Set(
    (hasTracked ? trackedIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean),
  );
  let pending = (Array.isArray(history) ? history : []).filter(
    (h) => h?.id && !already.has(String(h.id)),
  );
  if (!hasTracked) {
    const created = Number(boardCreatedMs) || 0;
    pending = pending.filter((h) => {
      const at = Number(h?.createdAt);
      if (!Number.isFinite(at) || at <= 0) return false;
      return !created || at > created;
    });
  }
  return pending;
}
